<template>
  <div class="square-page">
    <section class="square-toolbar">
      <div class="square-toolbar__copy">
        <div class="square-toolbar__title">广场管理</div>
        <div class="square-toolbar__desc">
          广场帖子的统一管理：搜索、查看图片/视频、上架（恢复可见）、下架（隐藏并算作审核未通过）、删除、恢复。
          只有「已通过」且未删除的帖子才会出现在小程序广场。
        </div>
      </div>
      <div class="square-toolbar__actions">
        <span class="square-count">共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="loading" @click="reload">
          {{ loading ? "刷新中" : "刷新" }}
        </button>
      </div>
    </section>

    <section class="square-card">
      <div class="square-filters">
        <input
          v-model="filters.keyword"
          class="filter-input"
          type="text"
          placeholder="搜索：帖子ID / 正文 / 作者昵称"
          @keyup.enter="reload"
        />
        <input v-model="filters.topicCode" class="filter-input filter-input--narrow" type="text" placeholder="话题编码" @keyup.enter="reload" />
        <select v-model="filters.visibilityScope" class="filter-select" @change="reload">
          <option value="">全部可见范围</option>
          <option value="public">公开</option>
          <option value="family">家庭</option>
          <option value="private">私密</option>
        </select>
        <select v-model="filters.auditStatus" class="filter-select" @change="reload">
          <option value="">全部审核状态</option>
          <option value="PASS">已通过</option>
          <option value="REVIEWING">审核中</option>
          <option value="REJECTED">未通过</option>
        </select>
        <select v-model="filters.deleted" class="filter-select" @change="reload">
          <option value="">全部帖子</option>
          <option value="0">未删除</option>
          <option value="1">已删除</option>
        </select>
        <button class="primary-button" type="button" @click="reload">搜索</button>
      </div>

      <div v-if="loading" class="list-state">正在加载广场帖子…</div>
      <div v-else-if="!rows.length" class="list-state">没有符合条件的帖子。</div>
      <div v-else class="post-list">
        <article v-for="row in rows" :key="String(row.id)" class="post-row">
          <div class="post-media">
            <img v-if="coverUrl(row)" class="post-thumb" :src="coverUrl(row)" alt="" />
            <div v-else class="post-thumb post-thumb--empty">{{ row.mediaType === "video" ? "视频" : "无图" }}</div>
          </div>
          <div class="post-main">
            <div class="post-head">
              <span class="post-id">#{{ row.id }}</span>
              <span class="post-author">{{ row.authorName || "用户" }}（UID {{ row.ownerUserId }}）</span>
              <span class="post-time">{{ formatTime(row.createdAt) }}</span>
            </div>
            <div class="post-content">{{ row.content || "（无正文）" }}</div>
            <div class="post-tags">
              <span class="tag">#{{ row.topicName || row.topicCode || "未分类" }}</span>
              <span class="tag">{{ visibilityLabel(row.visibilityScope) }}</span>
              <span class="tag" v-if="row.activityCampaignId">活动帖</span>
              <span class="stat">赞 {{ row.likeCount || 0 }}</span>
              <span class="stat">评 {{ row.commentCount || 0 }}</span>
              <span class="stat">藏 {{ row.favoriteCount || 0 }}</span>
            </div>
          </div>
          <div class="post-side">
            <span class="status-pill" :class="statusClass(row)">
              {{ statusLabel(row) }}
            </span>
            <button class="link-button" type="button" @click="updateStatus(row, 'PASS')" v-if="row.deleted === 1 || row.contentAuditStatus !== 'PASS'">
              上架
            </button>
            <button class="link-button" type="button" @click="updateStatus(row, 'REJECTED')" v-if="row.deleted === 0 && row.contentAuditStatus === 'PASS'">
              下架
            </button>
            <button class="link-button link-button--danger" type="button" @click="removePost(row)" v-if="row.deleted === 0">删除</button>
            <button class="link-button" type="button" @click="restorePost(row)" v-if="row.deleted === 1">恢复</button>
          </div>
        </article>
      </div>

      <div v-if="total > pageSize" class="pager">
        <button class="ghost-button" type="button" :disabled="page <= 1" @click="changePage(page - 1)">上一页</button>
        <span class="pager-text">第 {{ page }} / {{ totalPages }} 页 · 共 {{ total }} 条</span>
        <button class="ghost-button" type="button" :disabled="page >= totalPages" @click="changePage(page + 1)">下一页</button>
      </div>

      <div v-if="message" class="page-message" :class="{ 'page-message--error': messageError }">{{ message }}</div>
    </section>
  </div>
</template>

<script>
import { resolveBackendAssetUrl } from "../utils/apiEndpoint"
import {
  deleteAdminSquarePost,
  fetchAdminSquarePosts,
  restoreAdminSquarePost,
  updateAdminSquarePostStatus
} from "../api/squareAdmin"

export default {
  name: "SquareManageView",
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { keyword: "", topicCode: "", visibilityScope: "", auditStatus: "", deleted: "" },
      message: "",
      messageError: false
    }
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil((this.total || 0) / this.pageSize))
    }
  },
  created() {
    this.reload()
  },
  methods: {
    resolveFileUrl(value) {
      return resolveBackendAssetUrl(value)
    },
    coverUrl(row) {
      if (!row) return ""
      if (row.mediaType === "video") {
        return row.videoCoverUrl ? this.resolveFileUrl(row.videoCoverUrl) : ""
      }
      const list = row.imageUrls || []
      return list.length > 0 ? this.resolveFileUrl(list[0]) : ""
    },
    formatTime(value) {
      if (!value) return "-"
      return String(value).replace("T", " ").slice(0, 16)
    },
    visibilityLabel(scope) {
      if (scope === "family") return "家庭可见"
      if (scope === "private") return "仅自己"
      return "公开"
    },
    statusLabel(row) {
      if (row.deleted === 1) return "已删除"
      if (row.contentAuditStatus === "PASS") return "已上架"
      if (row.contentAuditStatus === "REJECTED") return "已下架"
      return "审核中"
    },
    statusClass(row) {
      if (row.deleted === 1) return "status-pill--deleted"
      if (row.contentAuditStatus === "PASS") return "status-pill--pass"
      if (row.contentAuditStatus === "REJECTED") return "status-pill--rejected"
      return "status-pill--reviewing"
    },
    setMessage(text, isError) {
      this.message = text
      this.messageError = !!isError
    },
    async reload() {
      this.loading = true
      try {
        const res = await fetchAdminSquarePosts({
          keyword: this.filters.keyword || undefined,
          topicCode: this.filters.topicCode || undefined,
          visibilityScope: this.filters.visibilityScope || undefined,
          auditStatus: this.filters.auditStatus || undefined,
          deleted: this.filters.deleted === "" ? undefined : Number(this.filters.deleted),
          page: this.page,
          pageSize: this.pageSize
        })
        const data = res.data || {}
        this.rows = data.records || []
        this.total = Number(data.total || 0)
      } catch (error) {
        this.setMessage((error && error.message) || "广场帖子加载失败", true)
      } finally {
        this.loading = false
      }
    },
    changePage(next) {
      this.page = Math.min(Math.max(1, next), this.totalPages)
      this.reload()
    },
    async updateStatus(row, auditStatus) {
      try {
        const res = await updateAdminSquarePostStatus(row.id, auditStatus)
        this.setMessage(res.message || "操作成功", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "操作失败", true)
      }
    },
    async removePost(row) {
      if (!window.confirm(`确定删除帖子 #${row.id} 吗？删除后小程序里不再展示。`)) {
        return
      }
      try {
        await deleteAdminSquarePost(row.id)
        this.setMessage("帖子已删除", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "删除失败", true)
      }
    },
    async restorePost(row) {
      try {
        await restoreAdminSquarePost(row.id)
        this.setMessage("帖子已恢复并上架", false)
        await this.reload()
      } catch (error) {
        this.setMessage((error && error.message) || "恢复失败", true)
      }
    }
  }
}
</script>

<style scoped>
.square-page {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.square-toolbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 22px 24px;
  border-radius: 16px;
  background: linear-gradient(120deg, #f0f7f2 0%, #ffffff 60%);
  border: 1px solid #dcebe1;
}

.square-toolbar__title {
  font-size: 20px;
  font-weight: 700;
  color: #1f2937;
}

.square-toolbar__desc {
  margin-top: 8px;
  max-width: 760px;
  font-size: 13px;
  line-height: 20px;
  color: #6b7280;
}

.square-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.square-count {
  font-size: 13px;
  color: #4b7a5c;
}

.square-card {
  padding: 20px 22px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #eceff3;
}

.square-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
}

.filter-select,
.filter-input {
  height: 38px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid #dfe3e8;
  font-size: 13px;
  color: #1f2937;
  box-sizing: border-box;
}

.filter-input {
  flex: 1;
  max-width: 300px;
}

.filter-input--narrow {
  max-width: 150px;
}

.primary-button,
.ghost-button {
  height: 38px;
  padding: 0 16px;
  border-radius: 8px;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
}

.primary-button {
  background: #111827;
  color: #ffffff;
}

.primary-button:disabled,
.ghost-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.ghost-button {
  background: #ffffff;
  border-color: #dfe3e8;
  color: #374151;
}

.list-state {
  padding: 26px 0;
  text-align: center;
  font-size: 13px;
  color: #9ca3af;
}

.post-list {
  display: flex;
  flex-direction: column;
}

.post-row {
  display: flex;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f1f3f6;
}

.post-media {
  flex-shrink: 0;
}

.post-thumb {
  width: 78px;
  height: 78px;
  border-radius: 10px;
  object-fit: cover;
  background: #f2f4f7;
}

.post-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #9ca3af;
}

.post-main {
  flex: 1;
  min-width: 0;
}

.post-head {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: #8b93a1;
}

.post-id {
  font-weight: 700;
  color: #111827;
}

.post-content {
  margin-top: 6px;
  font-size: 14px;
  line-height: 21px;
  color: #1f2937;
  word-break: break-all;
}

.post-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.tag {
  padding: 2px 8px;
  border-radius: 999px;
  background: #f2f4f7;
  font-size: 12px;
  color: #4b5563;
}

.stat {
  font-size: 12px;
  color: #8b93a1;
}

.post-side {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  flex-shrink: 0;
}

.status-pill {
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 12px;
  white-space: nowrap;
}

.status-pill--pass {
  background: #e7f6ec;
  color: #1d8a4b;
}

.status-pill--reviewing {
  background: #fff5e5;
  color: #b45309;
}

.status-pill--rejected {
  background: #fdecea;
  color: #b23f2c;
}

.status-pill--deleted {
  background: #f2f4f7;
  color: #8b93a1;
}

.link-button {
  padding: 0;
  border: none;
  background: none;
  font-size: 13px;
  color: #2563eb;
  cursor: pointer;
}

.link-button--danger {
  color: #dc2626;
}

.pager {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 14px;
}

.pager-text {
  font-size: 13px;
  color: #6b7280;
}

.page-message {
  margin-top: 12px;
  font-size: 13px;
  color: #1d8a4b;
}

.page-message--error {
  color: #dc2626;
}
</style>
